import posthogModule from 'posthog-js';
import { isPostHogCaptureAllowedByCmp } from './postHogConsentGate';

/** CRA 2 / webpack 4 interop — posthog-js ESM default export varies by version. */
const resolvePostHogClient = (mod) => {
  if (!mod) {
    return null;
  }
  if (typeof mod.init === 'function') {
    return mod;
  }
  if (mod.default && typeof mod.default.init === 'function') {
    return mod.default;
  }
  if (mod.posthog && typeof mod.posthog.init === 'function') {
    return mod.posthog;
  }
  return null;
};

const posthog = resolvePostHogClient(posthogModule);

let postHogEnabled = false;
let postHogInitInFlight = false;
let postHogSdkEverLoaded = false;
let pendingInitialPageView = false;
/** Blocks posthog.init after an explicit consent teardown until analytics is accepted again. */
let postHogInitPermitted = true;
/** Last-chance drop for queued SDK events (before_send) after decline or in-flight init. */
let postHogCaptureSuppressed = false;

function isCaptureAllowedByConsent() {
  return postHogInitPermitted && isPostHogCaptureAllowedByCmp();
}

const canCapture = () => {
  if (!isCaptureAllowedByConsent() || !postHogEnabled || !posthog) {
    return false;
  }
  if (typeof posthog.has_opted_out_capturing === 'function' && posthog.has_opted_out_capturing()) {
    return false;
  }
  return true;
};

export function allowPostHogInit() {
  postHogInitPermitted = true;
  postHogCaptureSuppressed = false;
}

export function blockPostHogInit() {
  postHogInitPermitted = false;
  postHogCaptureSuppressed = true;
}

export const isPostHogInitialized = () => postHogEnabled;

function setSessionRecordingDisabled(client, disabled) {
  if (!client || typeof client.set_config !== 'function') {
    return;
  }
  try {
    client.set_config({ disable_session_recording: disabled });
  } catch (e) {
    /* ignore */
  }
}

function startPostHogSessionRecording(client) {
  if (!client) {
    return;
  }
  setSessionRecordingDisabled(client, false);
  try {
    if (typeof client.startSessionRecording === 'function') {
      client.startSessionRecording();
    }
  } catch (e) {
    /* ignore */
  }
}

const MAX_EVENT_URL_LENGTH = 1024;

function truncateUrlForPostHog(url) {
  if (typeof url !== 'string' || url.length <= MAX_EVENT_URL_LENGTH) {
    return url;
  }
  return url.slice(0, MAX_EVENT_URL_LENGTH);
}

function sanitizeOutboundEvent(event) {
  if (!event || typeof event !== 'object') {
    return event;
  }
  const { properties } = event;
  if (!properties || typeof properties !== 'object') {
    return event;
  }
  const nextProps = { ...properties };
  ['$current_url', '$referrer', '$initial_referrer', '$session_entry_url'].forEach((key) => {
    if (typeof nextProps[key] === 'string') {
      nextProps[key] = truncateUrlForPostHog(nextProps[key]);
    }
  });
  return { ...event, properties: nextProps };
}

/** Opt in after CMP grant; clear stale persisted opt-out before re-enable (resume path). */
function optInPostHogCapturing(client) {
  if (!client) {
    return;
  }
  try {
    if (typeof client.clear_opt_in_out_capturing === 'function') {
      client.clear_opt_in_out_capturing();
    }
    if (typeof client.opt_in_capturing === 'function') {
      client.opt_in_capturing();
    }
  } catch (e) {
    /* ignore */
  }
}

function stopPostHogSessionRecording(client) {
  if (!client) {
    return;
  }
  try {
    if (typeof client.stopSessionRecording === 'function') {
      client.stopSessionRecording();
    }
  } catch (e) {
    /* ignore */
  }
  setSessionRecordingDisabled(client, true);
}

function finishLoaded(posthogInstance, environment) {
  postHogSdkEverLoaded = true;
  if (!isCaptureAllowedByConsent()) {
    try {
      posthogInstance.opt_out_capturing();
    } catch (e) {
      /* ignore */
    }
    stopPostHogSessionRecording(posthogInstance);
    postHogEnabled = false;
    postHogInitInFlight = false;
    pendingInitialPageView = false;
    return;
  }

  try {
    optInPostHogCapturing(posthogInstance);
    posthogInstance.register({
      appName: 'OE_PROFILE',
      environment: environment || 'UNKNOWN',
    });
    startPostHogSessionRecording(posthogInstance);
  } catch (e) {
    /* ignore */
  }

  postHogEnabled = true;
  postHogInitInFlight = false;

  if (pendingInitialPageView) {
    pendingInitialPageView = false;
    capturePageView();
  }
}

/**
 * @param {{ apiKey?: string, apiHost?: string, environment?: string, enabled?: boolean }} platformAnalytics
 * @param {{ queueInitialPageView?: boolean }} [options]
 * @returns {boolean} whether init was started
 */
export const initPostHog = (platformAnalytics, options = {}) => {
  if (
    !postHogInitPermitted
    || !isCaptureAllowedByConsent()
    || postHogEnabled
    || postHogInitInFlight
  ) {
    return false;
  }
  if (!platformAnalytics || platformAnalytics.enabled === false) {
    return false;
  }
  const { apiKey, apiHost, environment } = platformAnalytics;
  if (postHogSdkEverLoaded && posthog) {
    pendingInitialPageView = options.queueInitialPageView === true;
    finishLoaded(posthog, environment);
    return true;
  }
  if (!apiKey || !apiHost) {
    return false;
  }
  if (!posthog) {
    return false;
  }

  pendingInitialPageView = options.queueInitialPageView === true;
  postHogInitInFlight = true;

  posthog.init(apiKey, {
    api_host: apiHost,
    autocapture: false,
    capture_pageview: false,
    // Held off until finishLoaded confirms consent, then startPostHogSessionRecording.
    disable_session_recording: true,
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    person_profiles: 'identified_only',
    defaults: '2025-05-24',
    before_send: (event) => {
      if (postHogCaptureSuppressed || !isCaptureAllowedByConsent()) {
        return null;
      }
      return sanitizeOutboundEvent(event);
    },
    loaded: (posthogInstance) => {
      finishLoaded(posthogInstance, environment);
    },
  });

  return true;
};

export const shutdownPostHog = () => {
  blockPostHogInit();
  postHogEnabled = false;
  postHogInitInFlight = false;
  pendingInitialPageView = false;

  if (!posthog) {
    return;
  }

  try {
    if (typeof posthog.opt_out_capturing === 'function') {
      posthog.opt_out_capturing();
    }
    stopPostHogSessionRecording(posthog);
  } catch (e) {
    /* ignore SDK errors during teardown */
  }
};

export const capturePageView = () => {
  if (!canCapture()) {
    return;
  }
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    $pathname: window.location.pathname,
    $host: window.location.host,
    $referrer: document.referrer,
  });
};

export const capturePageLeave = (pageLeaveUrl) => {
  if (!canCapture() || !pageLeaveUrl) {
    return;
  }
  posthog.capture('$pageleave', {
    $current_url: pageLeaveUrl,
    $pathname: window.location.pathname,
    $host: window.location.host,
  });
};

export const captureUserEvent = (eventName, optionalProps) => {
  if (!canCapture()) {
    return;
  }
  posthog.capture(eventName, optionalProps || {});
};

export const captureProfileContactFormRendered = (optionalProps = {}) => {
  captureUserEvent('ProfileContactForm_RENDERED', {
    pathname: window.location.pathname,
    ...optionalProps,
  });
};

/** @internal test hook */
export const resetPostHogStateForTests = () => {
  postHogEnabled = false;
  postHogInitInFlight = false;
  postHogSdkEverLoaded = false;
  pendingInitialPageView = false;
  postHogInitPermitted = true;
  postHogCaptureSuppressed = false;
};
